import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Home.css';
import {
  HomeheroPrincipal, HomeHeroPotencia, HomeHeroMantenaice, HomeHeroSparePart,
  Excavadora_1, Retroexcavadora_1, Manipuladores_1, CargadoresFrontales_1,
  Agricultura_1, Construccion_1, Industrial_1, Oem_1, ResiduosReciclaje_1
} from '../../assets/images';
import { HomeHeroVideo } from '../../assets/videos';
import { jcb } from '../../api/apiClient';

const Home = () => {
  const [branches, setBranches] = useState({});
  const [branchData, setBranchData] = useState(null);
  const [activeBranch, setActiveBranch] = useState('villahermosa');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // ============================================================
  // Cargar sucursales desde la API
  // ============================================================
  useEffect(() => {
    let isMounted = true;
    const loadBranches = async () => {
      try {
        const { data } = await jcb.get('/branches');
        if (!isMounted) return;
        const list = data.branches || {};
        setBranches(list);

        const first = list[activeBranch] || Object.values(list)[0];
        if (first) setBranchData(first);
      } catch (err) {
        console.error('Error cargando sucursales:', err);
      } finally {
        if (isMounted) setLoadingBranches(false);
      }
    };
    loadBranches();
    return () => { isMounted = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const branchKeys = Object.keys(branches);

  const selectBranch = (id) => {
    const b = branches[id];
    if (!b) return;
    setBranchData(b);
    setActiveBranch(id);
  };

  const handleVideoRef = (video) => {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const tryPlay = () => {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          const handleInteraction = () => {
            video.play().catch(() => { });
            document.removeEventListener('touchstart', handleInteraction);
            document.removeEventListener('click', handleInteraction);
          };
          document.addEventListener('touchstart', handleInteraction, { once: true });
          document.addEventListener('click', handleInteraction, { once: true });
        });
      }
    };

    tryPlay();
  };

  return (
    <div className="home-container">
      <Helmet>
        <title>JCB AZTECA</title>
        <meta name="description" content="Distribuidor autorizado JCB en el sureste de México. Venta de maquinaria pesada, refacciones originales y servicio técnico especializado en Villahermosa, Mérida, Veracruz y Puerto Morelos." />
        <meta name="keywords" content="JCB, maquinaria pesada, retroexcavadoras, excavadoras, minicargadores, cargadores frontales, manipuladores telescópicos, refacciones JCB, servicio técnico, Villahermosa, Mérida, Veracruz, Puerto Morelos, JCB Azteca" />
        <link rel="canonical" href="https://azteca-site.org/" />

        <meta property="og:type" content="website" />
        <meta property="og:title" content="JCB AZTECA" />
        <meta property="og:description" content="Distribuidor autorizado JCB en el sureste de México. Maquinaria pesada, refacciones originales y servicio técnico especializado." />
        <meta property="og:url" content="https://azteca-site.org/" />
        <meta property="og:image" content="https://azteca-site.org/og/home-jcb-azteca.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="JCB Azteca - Distribuidor autorizado de maquinaria JCB en el sureste de México" />
        <meta property="og:locale" content="es_MX" />
        <meta property="og:site_name" content="JCB Azteca" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="JCB AZTECA" />
        <meta name="twitter:description" content="Distribuidor autorizado JCB en el sureste de México. Maquinaria pesada, refacciones y servicio técnico." />
        <meta name="twitter:image" content="https://azteca-site.org/og/home-jcb-azteca.jpg" />
        <meta name="twitter:image:alt" content="JCB Azteca - Maquinaria JCB en el sureste de México" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://azteca-site.org/#organization",
                "name": "JCB Azteca",
                "url": "https://azteca-site.org/",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://azteca-site.org/og/logo-jcb-azteca.png",
                  "width": 512,
                  "height": 512
                },
                "image": "https://azteca-site.org/og/home-jcb-azteca.jpg",
                "description": "Distribuidor autorizado JCB en el sureste de México. Maquinaria pesada, refacciones y servicio técnico.",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "+52-993-478-4100",
                  "contactType": "customer service",
                  "areaServed": "MX",
                  "availableLanguage": ["es"]
                },
                "sameAs": []
              },
              {
                "@type": "WebSite",
                "@id": "https://azteca-site.org/#website",
                "url": "https://azteca-site.org/",
                "name": "JCB Azteca",
                "inLanguage": "es-MX",
                "publisher": { "@id": "https://azteca-site.org/#organization" },
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": "https://azteca-site.org/spare-parts?q={search_term_string}",
                  "query-input": "required name=search_term_string"
                }
              },
              {
                "@type": "LocalBusiness",
                "@id": "https://azteca-site.org/#villahermosa",
                "name": "JCB Azteca - Sede Villahermosa",
                "image": "https://azteca-site.org/og/home-jcb-azteca.jpg",
                "url": "https://azteca-site.org/",
                "telephone": "+52-993-478-4100",
                "priceRange": "$$$",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Carr. Vhsa-Cardenas Ría. Anacleto Canabal Km. 0.800",
                  "addressLocality": "Villahermosa",
                  "addressRegion": "Tabasco",
                  "postalCode": "86280",
                  "addressCountry": "MX"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 17.9882177,
                  "longitude": -92.9809236
                },
                "openingHoursSpecification": [
                  {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                    "opens": "08:00",
                    "closes": "18:00"
                  },
                  {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": "Saturday",
                    "opens": "08:00",
                    "closes": "13:00"
                  }
                ]
              },
              {
                "@type": "LocalBusiness",
                "@id": "https://azteca-site.org/#merida",
                "name": "JCB Azteca - Sede Mérida",
                "url": "https://azteca-site.org/",
                "telephone": "+52-993-277-1860",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Perif. de Mérida Lic. Manuel Berzunza, San José Tecoh II",
                  "addressLocality": "Mérida",
                  "addressRegion": "Yucatán",
                  "postalCode": "97297",
                  "addressCountry": "MX"
                }
              },
              {
                "@type": "LocalBusiness",
                "@id": "https://azteca-site.org/#veracruz",
                "name": "JCB Azteca - Sede Veracruz",
                "url": "https://azteca-site.org/",
                "telephone": "+52-993-342-7984",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Av. Valente Díaz, Parque Industrial Sta. Fe Lt. 24",
                  "addressLocality": "Veracruz",
                  "addressRegion": "Veracruz",
                  "postalCode": "91697",
                  "addressCountry": "MX"
                }
              },
              {
                "@type": "LocalBusiness",
                "@id": "https://azteca-site.org/#puerto-morelos",
                "name": "JCB Azteca - Sede Puerto Morelos",
                "url": "https://azteca-site.org/",
                "telephone": "+52-998-321-3342",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Carretera Playa del Carmen, Cancún - Chetumal KM 316",
                  "addressLocality": "Puerto Morelos",
                  "addressRegion": "Quintana Roo",
                  "postalCode": "77580",
                  "addressCountry": "MX"
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <section className="relative w-full h-[88vh] min-h-[640px] max-h-[960px] bg-dark-primary overflow-hidden flex items-end">
        <div className={`absolute inset-0 z-0 transition-opacity duration-1000 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}>
          <img
            alt="Showroom de maquinaria JCB Azteca en Villahermosa, Tabasco"
            title="Showroom JCB Azteca | Maquinaria JCB en Villahermosa"
            className="w-full h-full object-cover object-center"
            src={HomeheroPrincipal}
            width="1920"
            height="1080"
          />
        </div>

        <div className="absolute inset-0 z-0">
          <video
            ref={handleVideoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            controls={false}
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            aria-label="Video de presentación de maquinaria JCB Azteca"
            className={`w-full h-full object-cover object-center transition-opacity duration-1000 pointer-events-none ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoadedData={() => setVideoLoaded(true)}
            onCanPlay={() => setVideoLoaded(true)}
          >
            <source src={HomeHeroVideo} type="video/mp4" />
          </video>
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-dark-primary via-dark-primary/30 to-black/40 z-0"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-dark-primary/70 via-transparent to-transparent z-0"></div>

        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-6 sm:px-12 pb-16 sm:pb-24">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-semibold text-white tracking-tight-headline leading-[1.05] uppercase">
              Maquinaria que impulsa tu trabajo
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#maquinaria" className="font-display text-xs uppercase tracking-luxury font-semibold bg-jcb-yellow hover:bg-jcb-yellow-hover text-dark-primary px-8 py-4 transition-all duration-300" title="Ver catálogo de maquinaria JCB">
                Ver maquinaria
              </a>
              <a href="#contacto" className="font-display text-xs uppercase tracking-luxury font-medium border border-white/40 text-white hover:border-white hover:bg-white/10 px-8 py-4 transition-all duration-300" title="Solicitar cotización de maquinaria JCB">
                Solicitar cotización
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-28 sm:py-36 bg-surface-tinted scroll-mt-20" id="maquinaria">
        <div className="max-w-[1680px] mx-auto px-6 sm:px-12">
          <div className="border-b border-border-subtle pb-8 mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <span className="font-display text-xs uppercase tracking-luxury text-jcb-yellow font-semibold block mb-3">
                Tipos de Maquinaria
              </span>
              <h2 className="font-display text-3xl sm:text-5xl font-semibold text-dark-primary tracking-tight-headline uppercase">
                Encuentra el equipo que necesitas
              </h2>
            </div>
            <p className="text-sm sm:text-base text-text-muted font-light max-w-md">
              Conoce nuestra amplia gama de maquinaria diseñada para cada tipo de trabajo y sector productivo.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                name: 'Cargadores Frontales',
                slug: 'cargadoresfrontales',
                desc: 'Alta capacidad de carga para minería, agregados, acarreo y manejo de materiales pesados.',
                img: CargadoresFrontales_1,
                alt: 'Cargador Frontal JCB - Equipo de alta capacidad de carga para minería y agregados'
              },
              {
                name: 'Excavadoras',
                slug: 'excavadoras',
                desc: 'Rendimiento hidráulico superior para proyectos de alta exigencia y gran profundidad.',
                img: Excavadora_1,
                alt: 'Excavadora JCB sobre orugas - Máquina de excavación de alto rendimiento'
              },
              {
                name: 'Manipuladores Telescópicos',
                slug: 'manipuladorestelescopicos',
                desc: 'Alcance y precisión para elevación de materiales en construcción, agricultura e industria.',
                img: Manipuladores_1,
                alt: 'Manipulador Telescópico JCB Loadall - Equipo de elevación con gran alcance y precisión'
              },
              {
                name: 'Retroexcavadoras',
                slug: 'retroexcavadora',
                desc: 'Versatilidad y potencia para excavación, zanjado y carga en todo tipo de terrenos.',
                img: Retroexcavadora_1,
                alt: 'Retroexcavadora JCB - La retroexcavadora más vendida del mundo desde 1953'
              }
            ].map((item, index) => (
              <Link
                key={index}
                to={`/machinery/${item.slug}`}
                className="group bg-white border border-border-subtle overflow-hidden hover:border-jcb-yellow hover:shadow-lg transition-all duration-300 block"
                title={`${item.name} JCB | JCB Azteca`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-surface-subtle">
                  <img
                    alt={item.alt}
                    title={`${item.name} JCB | JCB Azteca`}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    src={item.img}
                    loading="lazy"
                    width="600"
                    height="450"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold uppercase tracking-wide text-dark-primary group-hover:text-jcb-yellow transition-colors">
                    {item.name}
                  </h3>
                  <p className="mt-2 text-sm text-text-muted leading-relaxed">{item.desc}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-text-muted mb-6">Conoce los otros 7 tipos de maquinaria disponibles</p>
            <Link to="/machinery" className="inline-flex items-center gap-3 font-display text-xs uppercase tracking-luxury font-semibold bg-dark-primary hover:bg-black text-white px-10 py-4 transition-all duration-300" title="Ver toda la maquinaria JCB">
              <span>Ver toda la maquinaria</span>
              <span className="material-symbols-outlined text-sm" aria-hidden="true">arrow_forward</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="w-full py-28 sm:py-36 bg-surface-subtle border-y border-border-subtle scroll-mt-20" id="refacciones">
        <div className="max-w-[1680px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="font-display text-xs uppercase tracking-luxury text-jcb-yellow font-semibold block mb-3">
                Servicio & Postventa
              </span>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold uppercase tracking-tight-headline text-dark-primary leading-tight">
                Refacciones Y Aditamentos
              </h2>
              <p className="mt-6 text-base text-text-muted leading-relaxed font-normal max-w-lg">
                Diseñados siguiendo los más altos estándares y sometidos a rigurosas pruebas de durabilidad, los repuestos originales JCB garantizan el máximo tiempo de actividad, eficiencia operativa y vida útil de su máquina.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/spare-parts"
                  className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-semibold bg-dark-primary text-white hover:bg-black px-8 py-4 transition-all"
                  title="Buscar refacciones JCB originales"
                >
                  Buscar refacciones
                </Link>
              </div>
            </div>

            <div className="relative bg-dark-primary rounded-lg">
              <img
                src={HomeHeroSparePart}
                alt="Refacciones y aditamentos originales JCB disponibles en JCB Azteca"
                title="Refacciones JCB originales | JCB Azteca"
                className="w-full h-full object-cover opacity-90"
                loading="lazy"
                width="800"
                height="600"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-28 sm:py-36 bg-white scroll-mt-20" id="industrias">
        <div className="max-w-[1680px] mx-auto px-6 sm:px-12">
          <div className="border-b border-border-subtle pb-8 mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <span className="font-display text-xs uppercase tracking-luxury text-jcb-yellow font-semibold block mb-3">
                Soluciones para cada sector
              </span>
              <h2 className="font-display text-3xl sm:text-5xl font-semibold text-dark-primary tracking-tight-headline uppercase">
                Maquinaria para tu industria
              </h2>
            </div>
            <p className="text-sm sm:text-base text-text-muted font-light max-w-md">
              Donde sea que trabajes, tenemos el equipo que necesitas para hacer tu labor más eficiente y productiva.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              {
                id: 'reciclaje-y-residuos',
                name: 'Residuos & Reciclaje',
                desc: 'Soluciones compactas y eficientes para manejo de materiales en plantas de reciclaje y centros de acopio.',
                img: ResiduosReciclaje_1,
                alt: 'Maquinaria JCB para Residuos y Reciclaje - Soluciones Wastemaster para plantas de reciclaje'
              },
              {
                id: 'oem',
                name: 'Soluciones OEM',
                desc: 'Componentes y sistemas personalizados para integración en equipos especializados de cualquier sector.',
                img: Oem_1,
                alt: 'Maquinaria JCB Soluciones OEM - Componentes y sistemas personalizados para equipos especializados'
              },
              {
                id: 'industrial',
                name: 'Industrial',
                desc: 'Equipos de carga, manipulación y logística para almacenes, puertos, plantas y centros de distribución.',
                img: Industrial_1,
                alt: 'Maquinaria JCB para Industrial - Montacargas Teletruk y equipos de logística'
              }
            ].map((item) => (
              <div key={item.id} className="group relative overflow-hidden border border-border-subtle hover:border-jcb-yellow hover:shadow-xl transition-all duration-500 aspect-[16/9]">
                <div className="absolute inset-0 z-0">
                  <img
                    src={item.img}
                    alt={item.alt}
                    title={`${item.name} | JCB Azteca`}
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                    width="800"
                    height="450"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/40 group-hover:from-black/80 group-hover:via-black/40 transition-all duration-500"></div>
                </div>
                <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-8 text-white">
                  <h3 className="font-display text-2xl sm:text-3xl font-semibold uppercase tracking-wide">{item.name}</h3>
                  <p className="mt-2 text-sm text-white/80 leading-relaxed max-w-xs">{item.desc}</p>
                  <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Link to={`/industries/${item.id}`} className="font-display text-[10px] uppercase tracking-luxury font-semibold text-jcb-yellow hover:text-white transition-colors inline-flex items-center gap-2" title={`Explorar ${item.name}`}>
                      <span>Explorar</span>
                      <span className="material-symbols-outlined text-sm" aria-hidden="true">arrow_forward</span>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
            {[
              {
                id: 'construccion',
                name: 'Construcción',
                desc: 'Equipos versátiles para excavación, carga, elevación y movimiento de tierras en todo tipo de proyectos.',
                img: Construccion_1,
                alt: 'Maquinaria JCB para Construcción - Retroexcavadoras, excavadoras y minicargadores'
              },
              {
                id: 'agricultura',
                name: 'Agricultura',
                desc: 'Maquinaria diseñada para siembra, cosecha, manejo de forrajes y labores diarias del campo.',
                img: Agricultura_1,
                alt: 'Maquinaria JCB para Agricultura - Manipuladores telescópicos y cargadores frontales'
              }
            ].map((item) => (
              <div key={item.id} className="group relative overflow-hidden border border-border-subtle hover:border-jcb-yellow hover:shadow-xl transition-all duration-500 aspect-[16/9]">
                <div className="absolute inset-0 z-0">
                  <img
                    src={item.img}
                    alt={item.alt}
                    title={`${item.name} | JCB Azteca`}
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                    width="800"
                    height="450"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/40 group-hover:from-black/80 group-hover:via-black/40 transition-all duration-500"></div>
                </div>
                <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-8 text-white">
                  <h3 className="font-display text-2xl sm:text-3xl font-semibold uppercase tracking-wide">{item.name}</h3>
                  <p className="mt-2 text-sm text-white/80 leading-relaxed max-w-xs">{item.desc}</p>
                  <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Link to={`/industries/${item.id}`} className="font-display text-[10px] uppercase tracking-luxury font-semibold text-jcb-yellow hover:text-white transition-colors inline-flex items-center gap-2" title={`Explorar ${item.name}`}>
                      <span>Explorar</span>
                      <span className="material-symbols-outlined text-sm" aria-hidden="true">arrow_forward</span>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative w-full py-32 sm:py-44 bg-dark-primary overflow-hidden flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            alt="Maquinaria JCB Azteca trabajando en proyectos de construcción"
            title="Potencia JCB Azteca"
            className="w-full h-full object-cover object-center filter brightness-[0.4] contrast-[1.1]"
            src={HomeHeroPotencia}
            loading="lazy"
            width="1920"
            height="1080"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-6 sm:px-12 text-center">
          <div className="max-w-4xl mx-auto flex flex-col items-center">
            <span className="font-display text-xs uppercase tracking-luxury text-jcb-yellow font-semibold mb-4 block">Ingeniería & Respaldo</span>
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold uppercase text-white tracking-tight-headline leading-tight">
              Potencia para los proyectos que importan
            </h2>
            <p className="mt-6 text-base sm:text-xl text-neutral-300 font-light max-w-2xl leading-relaxed">
              Flotas confiables diseñadas para mantener la productividad de tu operación en cada jornada de trabajo.
            </p>
            <div className="mt-10">
              <a href="#maquinaria" className="inline-block font-display text-xs uppercase tracking-luxury font-semibold bg-white text-dark-primary hover:bg-jcb-yellow hover:text-dark-primary px-10 py-4 transition-all duration-300" title="Conoce la maquinaria JCB">
                Conoce nuestra maquinaria
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-28 sm:py-36 bg-white scroll-mt-20" id="nosotros">
        <div className="max-w-[1680px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-6">
              <div className="relative aspect-[4/3] bg-surface-subtle overflow-hidden">
                <img
                  alt="Taller de servicio técnico especializado JCB Azteca"
                  title="Talleres especializados JCB Azteca"
                  className="w-full h-full object-cover object-center"
                  src={HomeHeroMantenaice}
                  loading="lazy"
                  width="800"
                  height="600"
                />
              </div>
            </div>
            <div className="lg:col-span-6 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-2 h-2 bg-jcb-yellow rounded-full"></span>
                <span className="font-display text-xs uppercase tracking-luxury text-text-muted font-medium">Servicio Especializado</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold uppercase tracking-tight-headline text-dark-primary leading-tight">
                Talleres Especializados
              </h2>
              <p className="mt-6 text-base text-text-muted leading-relaxed font-normal max-w-xl">
                Contamos con talleres y personal técnico capacitado para realizar el mantenimiento, diagnóstico y reparación de tu maquinaria, ayudándote a mantener tus equipos en óptimas condiciones de operación.
              </p>
              <div className="mt-10">
                <a href="#contacto" className="inline-flex items-center gap-3 font-display text-xs uppercase tracking-luxury text-dark-primary hover:text-jcb-yellow font-semibold transition-colors group" title="Solicitar servicio técnico JCB">
                  <span>Solicitar servicio</span>
                  <span className="material-symbols-outlined text-sm transition-transform group-hover:translate-x-1" aria-hidden="true">arrow_forward</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-28 sm:py-36 bg-white border-t border-border-subtle scroll-mt-20" id="red-sureste">
        <div className="max-w-[1680px] mx-auto px-6 sm:px-12">
          <div className="border-b border-border-subtle pb-8 mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-2 h-2 bg-jcb-yellow rounded-full inline-block"></span>
                <span className="font-display text-xs uppercase tracking-luxury text-text-muted font-medium">Cobertura Regional & Centros de Distribución</span>
              </div>
              <h2 className="font-display text-3xl sm:text-5xl font-semibold text-dark-primary tracking-tight-headline uppercase">Nuestras Bases</h2>
            </div>
            <div className="max-w-md">
              <p className="font-display text-xs uppercase tracking-luxury text-jcb-yellow font-semibold mb-2">Cuatro Bases. Un Mismo Respaldo.</p>
              <p className="text-sm text-text-muted leading-relaxed font-light">
                Maquinaria pesada inmediata, catálogo íntegro de refacciones originales y servicio técnico en campo con cobertura total peninsular y del Golfo.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5 flex flex-col">
              <div className="flex border-b border-border-medium mb-8 overflow-x-auto">
                {loadingBranches && (
                  <span className="text-xs text-text-muted italic py-3 px-4">
                    Cargando sucursales...
                  </span>
                )}
                {!loadingBranches && branchKeys.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => selectBranch(key)}
                    aria-label={`Ver sucursal ${branches[key]?.name || key}`}
                    className={`font-display text-xs uppercase tracking-luxury py-3 px-4 whitespace-nowrap transition-all ${activeBranch === key
                      ? 'text-dark-primary border-b-2 border-jcb-yellow font-semibold'
                      : 'text-text-muted hover:text-dark-primary border-b-2 border-transparent font-medium'
                      }`}
                  >
                    {branches[key]?.short_name || (key === 'puerto-morelos' ? 'P. Morelos' : key.charAt(0).toUpperCase() + key.slice(1))}
                  </button>
                ))}
              </div>

              {branchData ? (
                <div className="bg-surface-subtle p-8 sm:p-10 border border-border-subtle flex flex-col justify-between min-h-[380px]">
                  <div>
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-2xl sm:text-3xl font-semibold uppercase tracking-tight-headline text-dark-primary mb-6">{branchData.name}</h3>
                      <span className="font-display text-[10px] uppercase tracking-luxury bg-dark-primary text-white px-3 py-1 font-medium">{branchData.state}</span>
                    </div>
                    <div className="space-y-4 text-sm text-text-muted">
                      <div>
                        <span className="font-display text-[10px] uppercase tracking-luxury text-text-subtle block mb-1">Dirección & Patio de Maniobras</span>
                        <p className="text-dark-primary font-medium text-sm leading-relaxed">{branchData.address}</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                        <div>
                          <span className="font-display text-[10px] uppercase tracking-luxury text-text-subtle block mb-2">
                            Línea Directa
                          </span>
                          <p className="text-dark-primary font-medium font-display text-sm">
                            {branchData.phone}
                          </p>
                          <div className="flex items-center gap-4 mt-1">
                            <a
                              href={`tel:${branchData.phone.replace(/\D/g, '')}`}
                              className="text-dark-primary hover:text-jcb-yellow transition-colors"
                              title={`Llamar a ${branchData.name}`}
                              aria-label={`Llamar a ${branchData.name}`}
                            >
                              <span className="material-symbols-outlined text-2xl" aria-hidden="true">call</span>
                            </a>

                            <a
                              href={`https://wa.me/${branchData.phone.replace(/\D/g, '')}?text=${encodeURIComponent(
                                `Hola, me gustaría recibir información sobre maquinaria y refacciones JCB (${branchData.name}).`
                              )}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-dark-primary hover:text-jcb-yellow transition-colors"
                              title={`Enviar WhatsApp a ${branchData.name}`}
                              aria-label={`Enviar WhatsApp a ${branchData.name}`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-6 h-6"
                                aria-hidden="true"
                              >
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                              </svg>
                            </a>
                          </div>
                        </div>

                        <div>
                          <span className="font-display text-[10px] uppercase tracking-luxury text-text-subtle block mb-1">Horario</span>
                          <p className="text-dark-primary font-medium text-xs">{branchData.hours}</p>
                        </div>
                      </div>
                      <div>
                        <span className="font-display text-[10px] uppercase tracking-luxury text-text-subtle block mb-1">
                          Correo
                        </span>
                        <a
                          href={`mailto:${branchData.email}`}
                          className="text-dark-primary font-medium text-sm hover:text-jcb-yellow transition-colors break-all"
                        >
                          {branchData.email}
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="pt-8 mt-6 border-t border-border-medium flex flex-wrap items-center justify-between gap-4">
                    <a href={branchData.maplink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-display text-xs uppercase tracking-luxury font-semibold text-dark-primary hover:text-jcb-yellow transition-colors group" title={`Cómo llegar a ${branchData.name}`}>
                      <span>Cómo llegar</span>
                      <span className="material-symbols-outlined text-sm transition-transform group-hover:translate-x-1" aria-hidden="true">north_east</span>
                    </a>
                    <a href="#contacto" className="font-display text-xs uppercase tracking-luxury bg-dark-primary hover:bg-black text-white px-6 py-3 transition-all" title="Agendar cita en JCB Azteca">Agendar Cita</a>
                  </div>
                </div>
              ) : (
                <div className="bg-surface-subtle p-8 sm:p-10 border border-border-subtle flex items-center justify-center min-h-[380px]">
                  <span className="text-sm text-text-muted italic">
                    {loadingBranches ? 'Cargando información...' : 'No hay sucursales disponibles.'}
                  </span>
                </div>
              )}
            </div>

            <div className="lg:col-span-7 bg-surface-subtle border border-border-subtle p-6 sm:p-10 relative overflow-hidden flex flex-col justify-between min-h-[460px]">
              <div className="flex items-center justify-between mb-4 z-10">
                <span className="flex items-center gap-2 text-[10px] uppercase tracking-luxury font-display text-text-muted">
                  <span className="w-2 h-2 rounded-full bg-jcb-yellow animate-pulse inline-block" aria-hidden="true"></span> {branchData?.name || 'Sucursal'}
                </span>
              </div>
              <div className="relative w-full h-[320px] sm:h-[380px] bg-white border border-border-subtle overflow-hidden">
                {branchData?.iframeSrc ? (
                  <iframe
                    src={branchData.iframeSrc}
                    className="w-full h-full"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                    title={`Mapa de ubicación - ${branchData.name} | JCB Azteca`}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-xs text-text-muted italic">Mapa no disponible.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-28 sm:py-36 bg-dark-primary scroll-mt-20" id="contacto">
        <div className="max-w-[1680px] mx-auto px-6 sm:px-12">
          <div className="flex flex-col items-center justify-center text-center">
            <h2 className="font-display text-3xl sm:text-5xl font-semibold uppercase tracking-tight-headline text-white leading-tight">
              ¿Buscas maquinaria o refacciones?
            </h2>
            <p className="mt-4 text-base text-neutral-300 leading-relaxed max-w-md">
              Solicita una propuesta personalizada para equipar tu operación con entrega inmediata.
            </p>
            <div className="mt-10">
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 font-display text-xs uppercase tracking-luxury font-semibold bg-jcb-yellow hover:bg-jcb-yellow-hover text-dark-primary px-10 py-4 transition-all duration-300"
                title="Ir a la página de contacto de JCB Azteca"
              >
                <span>Ir a Contacto</span>
                <span className="material-symbols-outlined text-sm" aria-hidden="true">arrow_forward</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;